import React, { useEffect, useState, useContext } from "react";
import { MainContext } from "../../contexts/MainContext";
import useHttpHook from "../../hooks/useHttpHook";
import useLanguage from "../../hooks/useLanguageHook";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";

const EarningsChartComponent = () => {
    const { userData } = useContext(MainContext);
    const { languageData } = useLanguage();
    const { httpRequest } = useHttpHook();
    const [availableYears, setAvailableYears] = useState([]);

    const [earningsData, setEarningsData] = useState([]);
    const [tickers, setTickers] = useState([]);
    const [selectedYears, setSelectedYears] = useState([new Date().getFullYear()]);
    const [selectedTicker, setSelectedTicker] = useState("all");

    const [totalEarnings, setTotalEarnings] = useState(0);
    const [allTimeEarnings, setAllTimeEarnings] = useState(null); // ✅ Store all-time earnings separately

    useEffect(() => {
        fetchAvailableYears();
        fetchAllTimeEarnings(); // ✅ Fetch all-time earnings once
    }, []);

    useEffect(() => {
        if (userData?.userId) {
            fetchEarnings();
        }
    }, [userData, selectedYears, selectedTicker]);

    async function fetchAvailableYears() {
        try {
            const response = await httpRequest("GET", `/b3/users/${userData.userId}/reports/earnings`);
            console.log("✅ Available Years:", response);

            if (response?.years?.length) {
                setAvailableYears(response.years);
            } else {
                const currentYear = new Date().getFullYear();
                setAvailableYears(Array.from({ length: currentYear - 2018 }, (_, i) => currentYear - i));
            }
        } catch (error) {
            console.error("❌ Error fetching available years:", error);
            const currentYear = new Date().getFullYear();
            setAvailableYears(Array.from({ length: currentYear - 2018 }, (_, i) => currentYear - i));
        }
    }

    async function fetchAllTimeEarnings() {
        try {
            const response = await httpRequest("GET", `/b3/users/${userData.userId}/reports/earnings?allTime=true`);
            console.log("✅ All-Time Earnings:", response);

            if (response?.totalOverall) {
                setAllTimeEarnings(response.totalOverall);
            }
        } catch (error) {
            console.error("❌ Error fetching all-time earnings:", error);
        }
    }

    async function fetchEarnings() {
        try {
            // ✅ If "all" is selected, use the stored all-time earnings instead of refetching
            if (selectedYears.length === 0 && selectedTicker === "all" && allTimeEarnings !== null) {
                setTotalEarnings(allTimeEarnings);
                return;
            }

            const yearsQuery = selectedYears.join(",");
            const query = `/b3/users/${userData.userId}/reports/earnings?years=${yearsQuery}${selectedTicker !== "all" ? `&tickers=${selectedTicker}` : ""}`;
            const response = await httpRequest("GET", query);
            console.log("✅ Earnings Data:", response);

            formatEarningsData(response);

            if (selectedTicker === "all") {
                extractTickers(response);
            }
        } catch (error) {
            console.error("❌ Error fetching earnings:", error);
        }
    }

    function formatEarningsData(data) {
        if (!data.earningsByYear) {
            console.error("No earnings data found.");
            setEarningsData([]);
            setTotalEarnings(0);
            return;
        }

        const months = Array.from({ length: 12 }, (_, i) => ({
            month: new Date(0, i).toLocaleString("default", { month: "short" }),
            total: 0,
        }));

        let total = 0;

        selectedYears.forEach((year) => {
            if (data.earningsByYear[year]) {
                const monthlyBreakdown = data.earningsByYear[year].monthlyBreakdown;

                Object.keys(monthlyBreakdown).forEach((month) => {
                    const monthIndex = parseInt(month, 10) - 1;
                    const monthEarnings = monthlyBreakdown[month].total;
                    months[monthIndex].total += monthEarnings;
                    total += monthEarnings;
                });
            }
        });

        setEarningsData(months);
        setTotalEarnings(total);
    }

    function extractTickers(data) {
        if (!data.earningsByYear) return;

        const uniqueTickers = new Set([...tickers]);

        selectedYears.forEach((year) => {
            if (data.earningsByYear[year]) {
                const monthlyBreakdown = data.earningsByYear[year].monthlyBreakdown;

                Object.values(monthlyBreakdown).forEach((month) => {
                    month.details.forEach((entry) => {
                        uniqueTickers.add(entry.ticker);
                    });
                });
            }
        });

        setTickers(Array.from(uniqueTickers));
    }

    return (
        <div className="colum is-8">
            <h3 className="subtitle">{languageData.b3Section.earningsChartTitle}</h3>

            {/* Dropdowns for selecting year and ticker */}
            <div className="columns">
                <div className="column">
                    <label>{languageData.b3Section.selectYear}</label>
                    <select
                        value={selectedYears}
                        onChange={(e) =>
                            setSelectedYears(Array.from(e.target.selectedOptions, option => parseInt(option.value)))
                        }
                        className="input"
                    >
                        {availableYears.map((year) => (
                            <option key={year} value={year}>{year}</option>
                        ))}
                    </select>
                </div>
                <div className="column">
                    <label>{languageData.b3Section.selectTicker}</label>
                    <select value={selectedTicker} onChange={(e) => setSelectedTicker(e.target.value)}
                            className="input">
                        <option value="all">{languageData.b3Section.allTickers}</option>
                        {tickers.map((ticker) => (
                            <option key={ticker} value={ticker}>{ticker}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* ✅ Display Total Accumulated Earnings */}
            <div className="notification is-light has-text-weight-bold">
                {languageData.b3Section.totalAccumulatedEarnings}:{" "}
                <span className="has-text-success">
                    {new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(
                        selectedYears.length === 0 && selectedTicker === "all" ? allTimeEarnings || 0 : totalEarnings
                    )}
                </span>
                <br/>
                <div>
                    <span>{languageData.b3Section.totalMonthlyAccumulatedEarnings}:{" "}</span>
                    <span className="has-text-success">
                    {new Intl.NumberFormat("pt-BR", {style: "currency", currency: "BRL"}).format(
                        selectedYears.length === 0 && selectedTicker === "all" ? allTimeEarnings / 12 || 0 : totalEarnings / 12
                    )}
                        /{languageData.b3Section.month}
                </span>
                </div>
            </div>


            {/* Earnings Bar Chart */}
            <ResponsiveContainer width="100%" height={400}>
                <BarChart data={earningsData} margin={{top: 20, right: 30, left: 50, bottom: 30}}>
                    <XAxis dataKey="month"/>
                    <YAxis tickFormatter={(value) =>
                        new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value)
                    } />
                    <Tooltip formatter={(value) =>
                        new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value)
                    } />
                    <Legend />
                    <Bar dataKey="total" fill="#04D1B2" />
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
};

export default EarningsChartComponent;
